import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getCountFromServer,
  deleteDoc,
  runTransaction,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { prepararFotoProducto } from '../../../core/media/prepararFotoProducto.js';
import { PRECIO_MAX_ARS } from '../../../core/catalog/precioLimites.js';
import { withTimeout } from '../../../core/network/timeout.js';
import { createWithFirestoreRetry } from '../../../core/firestore/withFirestoreRetry.js';
import {
  esNegocioPremiumPerfil,
  esUsuarioPremiumPerfil,
  LIMITE_PUBLICACIONES_ACTIVAS_FREE,
  LIMITE_PUBLICACIONES_ACTIVAS_PREMIUM,
  normalizarPlan,
  premiumHastaSigueVigente,
} from '../../../core/monetizacion/planes.js';
import { BADGE_VENDIDO_HORAS, TRIAL_DIAS } from '../../../core/monetizacion/vendedorNiveles.js';
import { snapshotReputacionParaProducto } from '../../../core/reputation/reputacionVendedor.js';

/** Error.code al superar publicaciones activas (modal dedicado en UI). */
export const MLV_ERR_LIMITE_PUBLICACIONES_ACTIVAS = 'MLV_LIMITE_PUBLICACIONES_ACTIVAS';
import {
  evaluarBloqueoPublicar,
  montoComisionArs,
  nivelParaComision,
  porcentajeComisionPorNivel,
  ventanaVentasTsActualizada,
} from '../domain/vendedorNivelRules.js';

const DESCRIPCION_PUBLICACION_MAX_LEN = 600;

function precioParaComisionDesdeProducto(pdata) {
  const raw = pdata?.precio ?? pdata?.precio_ars;
  const n = typeof raw === 'number' ? raw : Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

export function createProductosRepository({ getFirebase, profileRepo }) {
  const withRetry = createWithFirestoreRetry({ getFirebase });

  async function publicarProducto(
    uid,
    { titulo, precio, descripcion, files, categoria_id, estado_producto }
  ) {
    const n = Number(precio);
    if (!Number.isFinite(n) || n < 0) {
      throw new Error('Precio inválido.');
    }
    if (n > PRECIO_MAX_ARS) {
      throw new Error(
        `El precio máximo permitido es $ ${PRECIO_MAX_ARS.toLocaleString('es-AR')} ARS.`
      );
    }
    const descTrim = String(descripcion ?? '').trim();
    if (descTrim.length > DESCRIPCION_PUBLICACION_MAX_LEN) {
      throw new Error(
        `La descripción puede tener hasta ${DESCRIPCION_PUBLICACION_MAX_LEN} caracteres.`
      );
    }
    const { db, storage } = getFirebase();
    const perfilSnap = await getDoc(doc(db, 'usuarios', uid));
    const perfil = perfilSnap.exists() ? perfilSnap.data() : {};
    const bloq = evaluarBloqueoPublicar(
      perfil?.comision_deuda_ars,
      perfil?.saldo_favor_ars,
      perfil?.comision_ventas_impagas_count
    );
    if (bloq.bloqueado) {
      throw new Error(bloq.motivo || 'Regularizá la comisión pendiente para seguir publicando.');
    }
    const vig = premiumHastaSigueVigente(perfil);
    const esMarketPremium = vig && esNegocioPremiumPerfil(perfil);
    const esUserPremium = vig && esUsuarioPremiumPerfil(perfil);
    const plan = normalizarPlan(perfil?.plan);
    const limiteActivas = esUserPremium || esMarketPremium
      ? LIMITE_PUBLICACIONES_ACTIVAS_PREMIUM
      : LIMITE_PUBLICACIONES_ACTIVAS_FREE;

    const qActivas = query(
      collection(db, 'productos'),
      where('vendedor_uid', '==', uid),
      where('estado', '==', 'publicado')
    );
    const aggActivas = await getCountFromServer(qActivas);
    const activas = Number(aggActivas.data()?.count || 0);
    if (activas >= limiteActivas) {
      const nombrePlan = esMarketPremium
        ? 'Market Premium'
        : esUserPremium
          ? 'User Premium'
          : 'Free';
      const err = new Error(
        `Alcanzaste el máximo de ${limiteActivas} publicaciones activas (${nombrePlan}). Liberá cupos con «¡Lo vendí!» o ampliá tu plan.`
      );
      err.code = MLV_ERR_LIMITE_PUBLICACIONES_ACTIVAS;
      err.esPremium = Boolean(esUserPremium || esMarketPremium);
      err.limiteActivas = limiteActivas;
      throw err;
    }

    const UPLOAD_TIMEOUT_MS = 90000;
    const fotosUrls = await Promise.all(
      files.map(async (file, i) => {
        const optimizada = await prepararFotoProducto(file);
        const uq =
          typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID().replace(/-/g, '').slice(0, 12)
            : `${Math.random().toString(36).slice(2, 12)}`;
        const r = ref(
          storage,
          `productos/${uid}/${Date.now()}_${i}_${uq}_${optimizada.name}`
        );
        await withRetry(
          () =>
            withTimeout(
              uploadBytes(r, optimizada),
              UPLOAD_TIMEOUT_MS,
              'La subida de imagen tardó demasiado. Probá con mejor señal o intentá de nuevo.'
            ),
          2,
          1200
        );
        return withRetry(
          () =>
            withTimeout(
              getDownloadURL(r),
              15000,
              'No se pudo obtener la URL pública de la foto.'
            ),
          2,
          800
        );
      })
    );

    const telefonoContacto = String(perfil?.telefono || '').trim();

    const snapNivel = profileRepo.computarResumenNivel(perfil);
    const snapRep = snapshotReputacionParaProducto(perfil);
    const prodRef = await withTimeout(
      addDoc(collection(db, 'productos'), {
        vendedor_uid: uid,
        titulo: String(titulo).trim(),
        precio: n,
        descripcion: descTrim,
        categoria_id: categoria_id || 'general',
        estado_producto: estado_producto || 'usado',
        fotos: fotosUrls,
        estado: 'publicado',
        telefono_contacto: telefonoContacto || null,
        vendedor_plan: plan,
        negocio_premium: esMarketPremium,
        creadoEn: serverTimestamp(),
        listado_catalogo_vendido: false,
        vendedor_nivel_snapshot: snapNivel.nivelEfectivo,
        ...snapRep,
      }),
      15000,
      'No se pudo guardar la publicación en la base de datos.'
    );
    void prodRef;

    const qActivasNuevo = query(
      collection(db, 'productos'),
      where('vendedor_uid', '==', uid),
      where('estado', '==', 'publicado')
    );
    const aggNuevo = await getCountFromServer(qActivasNuevo);
    const nActivas = Number(aggNuevo.data()?.count || 0);
    const uRef = doc(db, 'usuarios', uid);
    const uSnap = await getDoc(uRef);
    const u = uSnap.exists() ? uSnap.data() : {};
    if (nActivas === 1 && !u.primera_publicacion_en) {
      await updateDoc(uRef, {
        primera_publicacion_en: serverTimestamp(),
        trial_nivel5_hasta: Timestamp.fromMillis(
          Date.now() + TRIAL_DIAS * 24 * 60 * 60 * 1000
        ),
        actualizadoEn: serverTimestamp(),
      });
    }
  }

  async function marcarComoVendido(uid, productId, options = {}) {
    const { db } = getFirebase();
    const pRef = doc(db, 'productos', String(productId));
    const uRef = doc(db, 'usuarios', uid);
    const ventaRef = doc(collection(db, 'usuarios', uid, 'ventas_vendedor'));
    const compradorTag = String(options?.compradorTag || '').trim();

    await runTransaction(db, async (tx) => {
      const pSnap = await tx.get(pRef);
      const uSnap = await tx.get(uRef);
      if (!pSnap.exists()) throw new Error('No encontramos esa publicación.');
      const pdata = pSnap.data();
      if (pdata.vendedor_uid !== uid) throw new Error('No podés modificar una publicación de otro vendedor.');
      if (pdata.estado !== 'publicado') throw new Error('Solo podés marcar como vendida una publicación activa.');

      const u = uSnap.exists() ? uSnap.data() || {} : {};
      const ahora = Date.now();
      const ventasTs = ventanaVentasTsActualizada(u.ventas_ts_window_ms || [], ahora);
      const ventasEnVentana = ventasTs.length;

      let enTrial = false;
      const th = u.trial_nivel5_hasta;
      if (th?.toMillis) enTrial = ahora < th.toMillis();

      const nivelCom = nivelParaComision({
        ventasEnVentana,
        enTrialNivel5: enTrial,
      });
      const pct = porcentajeComisionPorNivel(nivelCom);
      const precio = precioParaComisionDesdeProducto(pdata);
      const monto = montoComisionArs(precio, pct);

      const deudaPrev = Number(u.comision_deuda_ars) || 0;
      const saldoPrev = Number(u.saldo_favor_ars) || 0;
      const deudaBruta = deudaPrev + monto;
      const nuevaDeuda = Math.round(Math.max(0, deudaBruta - saldoPrev) * 100) / 100;
      const nuevoSaldo = Math.round(Math.max(0, saldoPrev - deudaBruta) * 100) / 100;
      const imp = (Number(u.comision_ventas_impagas_count) || 0) + 1;
      const totalApp = (Number(u.comision_total_app_ars) || 0) + monto;

      const badgeHasta = Timestamp.fromMillis(
        ahora + BADGE_VENDIDO_HORAS * 60 * 60 * 1000
      );

      tx.update(pRef, {
        estado: 'vendido',
        vendido_en: serverTimestamp(),
        vendido_precio_snapshot: precio,
        mostrar_badge_vendido_hasta: badgeHasta,
        listado_catalogo_vendido: true,
        actualizadoEn: serverTimestamp(),
      });
      tx.update(uRef, {
        ventas_ts_window_ms: ventasTs,
        comision_deuda_ars: nuevaDeuda,
        saldo_favor_ars: nuevoSaldo,
        comision_ventas_impagas_count: imp,
        comision_total_app_ars: totalApp,
        actualizadoEn: serverTimestamp(),
      });
      tx.set(ventaRef, {
        producto_id: String(productId),
        precio,
        pct_comision: pct,
        monto_comision_ars: monto,
        nivel_comision_usado: nivelCom,
        fecha_venta: serverTimestamp(),
        comprador_user_tag: compradorTag || null,
        pagada: false,
      });
    });

    return { ok: true };
  }

  async function listarMisPublicaciones(uid) {
    const { db } = getFirebase();
    const q = query(
      collection(db, 'productos'),
      where('vendedor_uid', '==', uid),
      where('estado', '==', 'publicado')
    );
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  }

  async function contarMisPublicaciones(uid) {
    const { db } = getFirebase();
    const q = query(collection(db, 'productos'), where('vendedor_uid', '==', uid));
    const agg = await getCountFromServer(q);
    return agg.data().count;
  }

  async function eliminarPublicacion(uid, productId) {
    const { db } = getFirebase();
    const refDoc = doc(db, 'productos', productId);
    const snap = await getDoc(refDoc);
    if (!snap.exists()) throw new Error('No encontramos esa publicación.');
    if (snap.data().vendedor_uid !== uid) throw new Error('No podés borrar una publicación de otro vendedor.');
    if (snap.data().estado === 'vendido') {
      throw new Error('Las publicaciones vendidas no se eliminan; quedan registradas.');
    }
    await deleteDoc(refDoc);
  }

  return {
    publicarProducto,
    listarMisPublicaciones,
    contarMisPublicaciones,
    eliminarPublicacion,
    marcarComoVendido,
  };
}
